import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { PACKAGE_SLOT_COUNT } from "@/constants/tribopayPackages";

export interface CooldownInfo {
  isOnCooldown: boolean;
  hoursRemaining: number;
  purchasedAt: Date;
}

export interface UsePurchaseCooldownReturn {
  cooldowns: Record<string, CooldownInfo>;
  isLoading: boolean;
  refetch: () => void;
}

const COOLDOWN_HOURS = 24;

const calculateHoursRemaining = (purchasedAt: Date): number => {
  const purchaseTime = new Date(purchasedAt).getTime();
  const cooldownEnd = purchaseTime + COOLDOWN_HOURS * 60 * 60 * 1000;
  const now = Date.now();
  const remaining = cooldownEnd - now;

  if (remaining <= 0) return 0;

  return Math.ceil(remaining / (60 * 60 * 1000));
};

// Extrair base package ID (ex: pack_supreme_slot2 -> pack_supreme)
const getBasePackageId = (packageId: string): string => {
  const slotMatch = packageId.match(/^(.+)_slot\d+$/);
  return slotMatch ? slotMatch[1] : packageId;
};

// Extrair slot index do package_id (ex: pack_supreme_slot2 -> 2)
const getSlotIndex = (packageId: string): number | null => {
  const slotMatch = packageId.match(/_slot(\d+)$/);
  return slotMatch ? parseInt(slotMatch[1], 10) : null;
};

export const usePurchaseCooldown = (): UsePurchaseCooldownReturn => {
  const { user } = useAuth();
  const [cooldowns, setCooldowns] = useState<Record<string, CooldownInfo>>({});
  const [isLoading, setIsLoading] = useState(false);

  const fetchCooldowns = useCallback(async () => {
    if (!user) {
      setCooldowns({});
      return;
    }

    setIsLoading(true);

    try {
      // Calculate the timestamp for 24 hours ago
      const twentyFourHoursAgo = new Date(Date.now() - COOLDOWN_HOURS * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from("tribopay_transactions")
        .select("package_id, created_at")
        .eq("user_id", user.id)
        .eq("status", "paid")
        .eq("credits_added", true)
        .gte("created_at", twentyFourHoursAgo)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching cooldown data:", error);
        setCooldowns({});
        return;
      }

      // Build cooldowns map considering slot rotation
      const cooldownMap: Record<string, CooldownInfo> = {};
      
      // Agrupar transações por base package ID
      const packageTransactions: Record<string, Array<{ packageId: string; createdAt: Date }>> = {};
      
      for (const transaction of data || []) {
        const basePackageId = getBasePackageId(transaction.package_id);
        
        if (!packageTransactions[basePackageId]) {
          packageTransactions[basePackageId] = [];
        }
        
        packageTransactions[basePackageId].push({
          packageId: transaction.package_id,
          createdAt: new Date(transaction.created_at),
        });
      }

      // Processar cooldowns por pacote
      for (const [basePackageId, transactions] of Object.entries(packageTransactions)) {
        const slotCount = PACKAGE_SLOT_COUNT[basePackageId];
        
        if (slotCount) {
          // Pacote com slots: só bloquear se TODOS os slots estiverem em cooldown
          const usedSlots = new Set<number>();
          let earliestPurchase: Date | null = null;
          
          for (const tx of transactions) {
            const slotIndex = getSlotIndex(tx.packageId);
            
            if (slotIndex) {
              usedSlots.add(slotIndex);
            } else {
              // Transações antigas sem sufixo = slot 1
              usedSlots.add(1);
            }
            
            // Rastrear a compra mais antiga (para mostrar quando o primeiro slot libera)
            if (!earliestPurchase || tx.createdAt < earliestPurchase) {
              earliestPurchase = tx.createdAt;
            }
          }
          
          // Só marcar como cooldown se TODOS os slots estiverem bloqueados
          if (usedSlots.size >= slotCount && earliestPurchase) {
            const hoursRemaining = calculateHoursRemaining(earliestPurchase);
            
            if (hoursRemaining > 0) {
              cooldownMap[basePackageId] = {
                isOnCooldown: true,
                hoursRemaining,
                purchasedAt: earliestPurchase,
              };
            }
          }
        } else {
          // Pacotes sem slots: comportamento atual (bloquear após 1 compra)
          const mostRecentTx = transactions[0];
          
          if (mostRecentTx) {
            const hoursRemaining = calculateHoursRemaining(mostRecentTx.createdAt);
            
            if (hoursRemaining > 0) {
              cooldownMap[basePackageId] = {
                isOnCooldown: true,
                hoursRemaining,
                purchasedAt: mostRecentTx.createdAt,
              };
            }
          }
        }
      }

      setCooldowns(cooldownMap);
    } catch (error) {
      console.error("Error in fetchCooldowns:", error);
      setCooldowns({});
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchCooldowns();
  }, [fetchCooldowns]);

  // Update cooldowns every minute while mounted
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      // Recalculate hours remaining without refetching
      setCooldowns((prev) => {
        const updated: Record<string, CooldownInfo> = {};
        
        for (const [packageId, info] of Object.entries(prev)) {
          const hoursRemaining = calculateHoursRemaining(info.purchasedAt);
          
          if (hoursRemaining > 0) {
            updated[packageId] = {
              ...info,
              hoursRemaining,
            };
          }
          // If hoursRemaining is 0, we don't include it (cooldown expired)
        }
        
        return updated;
      });
    }, 60 * 1000); // Every minute

    return () => clearInterval(interval);
  }, [user]);

  return {
    cooldowns,
    isLoading,
    refetch: fetchCooldowns,
  };
};
